import { useEffect, useState } from 'react'
import { Download, Map as MapIcon } from 'lucide-react'
import { ContentSkeleton } from '@/components/loading/Skeleton'
import { Button, Checkbox, Dialog, DialogClose, EmptyState, Notice, Select, useToast } from '@/components/play'
import { api } from '@/util/api'
import type { EventRoute, LivelabsMapsRace, LivelabsMapsRacesResponse } from '@/types/events'

interface Props {
  eventId: string
  /** Courses were imported: reload the event's routes. */
  onImported: () => void
}

interface ApiError {
  response?: { data?: { error?: { errors?: string[]; message?: string } } }
}

function errorMessage(failure: unknown, fallback: string): string {
  const error = (failure as ApiError).response?.data?.error
  return error?.errors?.join('\n') || error?.message || fallback
}

const km = new Intl.NumberFormat('en', { maximumFractionDigits: 1 })

/** Course settings' second way in: a race's courses from LiveLabs Maps, imported as routes on the server. */
export function LivelabsMapsImport({ eventId, onImported }: Props) {
  const { toast } = useToast()
  const [open, setOpen] = useState(false)
  const [races, setRaces] = useState<LivelabsMapsRace[] | null>(null)
  const [raceId, setRaceId] = useState<string | null>(null)
  const [courseIds, setCourseIds] = useState<string[]>([])
  const [loadError, setLoadError] = useState('')
  const [attempt, setAttempt] = useState(0)
  const [importError, setImportError] = useState('')
  const [importing, setImporting] = useState(false)

  const chooseRace = (race: LivelabsMapsRace | undefined) => {
    setRaceId(race?.id ?? null)
    setCourseIds(race?.courses.map((course) => course.id) ?? [])
    setImportError('')
  }

  useEffect(() => {
    if (!open || races) return
    let cancelled = false
    setLoadError('')
    api
      .get<LivelabsMapsRacesResponse>('/livelabs_maps/races')
      .then((response) => {
        if (cancelled) return
        setRaces(response.data)
        if (response.data.length === 1) chooseRace(response.data[0])
      })
      .catch((failure) => {
        if (!cancelled) setLoadError(errorMessage(failure, 'LiveLabs Maps couldn’t be reached. Try again.'))
      })
    return () => {
      cancelled = true
    }
  }, [open, races, attempt])

  const race = races?.find((candidate) => candidate.id === raceId)

  async function importCourses() {
    if (!race || courseIds.length === 0 || importing) return
    setImporting(true)
    setImportError('')
    try {
      const response = await api.post<{ data: EventRoute[] }>(`/events/${eventId}/routes/import`, {
        source: 'livelabs_maps',
        race_id: race.id,
        course_ids: courseIds,
      })
      const count = response.data.length
      toast({ title: count === 1 ? 'Course imported' : `${count} courses imported`, description: race.name, tone: 'success' })
      setOpen(false)
      onImported()
    } catch (failure) {
      // The notice's title says what failed; its text gives the server's reason or the next step.
      setImportError(errorMessage(failure, 'Try again.'))
    } finally {
      setImporting(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={setOpen}
      trigger={
        <Button variant="secondary">
          <Download aria-hidden="true" />
          Import from LiveLabs Maps
        </Button>
      }
      title="Import from LiveLabs Maps"
      description="Bring in a published race's courses."
      footer={
        <>
          <DialogClose>Cancel</DialogClose>
          <Button onClick={() => void importCourses()} disabled={!race || courseIds.length === 0} loading={importing}>
            Import courses
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {!races && !loadError && <ContentSkeleton label="Loading races" variant="list" count={2} />}
        {loadError && (
          <Notice
            tone="error"
            alert
            action={<Button variant="secondary" size="compact" onClick={() => setAttempt((current) => current + 1)}>Try again</Button>}
          >
            {loadError}
          </Notice>
        )}
        {races?.length === 0 && (
          <EmptyState
            size="compact"
            headingLevel={3}
            icon={<MapIcon />}
            title="No published races yet"
            description="Races appear here once they’re published on LiveLabs Maps."
          />
        )}
        {races && races.length > 1 && (
          <Select
            label="Race"
            items={races.map((candidate) => ({ value: candidate.id, label: candidate.name, description: candidate.location ?? undefined }))}
            value={raceId}
            onValueChange={(value) => chooseRace(races.find((candidate) => candidate.id === value))}
            placeholder="Choose a race"
          />
        )}
        {race && (
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">{races && races.length === 1 ? race.name : 'Courses'}</legend>
            {race.courses.map((course) => (
              <Checkbox
                key={course.id}
                label={course.name}
                description={course.distance_km != null ? `${km.format(course.distance_km)} km` : undefined}
                checked={courseIds.includes(course.id)}
                onCheckedChange={(checked) =>
                  setCourseIds((current) => (checked ? [...current, course.id] : current.filter((id) => id !== course.id)))
                }
              />
            ))}
          </fieldset>
        )}
        {importError && (
          <Notice tone="error" alert title="The courses couldn’t be imported">
            {importError}
          </Notice>
        )}
      </div>
    </Dialog>
  )
}
